// ═══════════════════════════════════════════════════════════════
// V·IANet Flow — FlowViewerCanvas (Prompt 147: UX v9)
// No A3Sheet, minZoom dynamic, tooltip card, elegant nodes
// ═══════════════════════════════════════════════════════════════

import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { request } from '../../../../shared/services/api';
import {
  ReactFlow,
  Panel,
  MarkerType,
  useViewport,
  useReactFlow,
  type NodeTypes,
  type EdgeTypes,
  type NodeMouseHandler,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import type {
  FlowNode,
  FlowEdge,
  FlowNodeData,
  FlowPhase,
  Swimlane,
} from '../types/flowchart.types';

import {
  ActivityNode,
  DecisionNode,
  DocumentNode,
  SubprocessNode,
  StartNode,
  EndNode,
  ProcessRefNode,
  GovernanceNode,
  ResultNode,
} from '../editor/nodes';
import { OrthogonalEdge } from '../editor/edges/OrthogonalEdge';
import { FLOW_TOKENS as T } from '../shared/flowTokens';

import SwimlaneRenderer from '../shared/SwimlaneRenderer';
import NodeTooltip from './NodeTooltip';
import NodeDetailPanel from './NodeDetailPanel';

// ─── Node & Edge Type Registries ──────────────────────────────────────────────

const NODE_TYPE_MAP: NodeTypes = {
  ACTIVIDAD: ActivityNode,
  DECISION: DecisionNode,
  DOCUMENTO: DocumentNode,
  SUBPROCESO: SubprocessNode,
  INICIO: StartNode,
  FIN: EndNode,
  PROCESO_REF: ProcessRefNode,
  PROCESO_GOBIERNO: GovernanceNode,
  RESULTADO: ResultNode,
};

const EDGE_TYPE_MAP: EdgeTypes = {
  orthogonal: OrthogonalEdge,
};

// ─── A3 Constants ─────────────────────────────────────────────────────────────

const A3_W = T.canvas.a3Width;
const A3_H = T.canvas.a3Height;

// ─── Swimlane Viewport Wrapper ───────────────────────────────────────────────

function SwimlaneViewportLayerViewer({
  lanes,
  phases,
}: {
  lanes: { id: string; name: string; y: number; height: number }[];
  phases?: FlowPhase[];
}) {
  const { x, y, zoom } = useViewport();

  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width: A3_W * zoom,
        height: A3_H * zoom,
        transformOrigin: '0 0',
        pointerEvents: 'none',
        zIndex: 0,
        overflow: 'hidden',
      }}
    >
      <div
        style={{ transform: `scale(${zoom})`, transformOrigin: '0 0', width: A3_W, height: A3_H }}
      >
        <SwimlaneRenderer lanes={lanes} phases={phases} readOnly />
      </div>
    </div>
  );
}

// ─── Custom Viewer Controls ──────────────────────────────────────────────────

function CustomViewerControls({ onFitToCanvas }: { onFitToCanvas?: () => void }) {
  const { zoomIn, zoomOut } = useReactFlow();
  const s: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: 5,
    border: '1px solid #D4D9E4',
    background: '#fff',
    cursor: 'pointer',
    fontSize: 15,
    color: '#595959',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    fontFamily: 'inherit',
  };
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 16,
        left: 16,
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <button style={s} onClick={() => zoomIn()} title="Acercar">
        +
      </button>
      <button style={s} onClick={() => zoomOut()} title="Alejar">
        {'\u2212'}
      </button>
      <button style={s} onClick={onFitToCanvas} title="Ajustar A3">
        {'\u229F'}
      </button>
    </div>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface FlowViewerCanvasProps {
  nodes: FlowNode[];
  edges: FlowEdge[];
  phases?: FlowPhase[];
  swimlanes?: Swimlane[];
  onFitToCanvas?: () => void;
  onInit?: (fit: () => void) => void;
  onNavigateDocument?: (codigo: string) => void;
  onNavigateProcess?: (codigo: string) => void;
  procesoId?: string;
}

// ─── Viewer Styles (elegant nodes, connectors, handles hidden) ───────────────

const VIEWER_STYLES = `
.react-flow__handle { opacity: 0 !important; pointer-events: none !important; width: 1px !important; height: 1px !important; min-width: 0 !important; min-height: 0 !important; }
.react-flow__node.selected { outline: none !important; box-shadow: none !important; }
.react-flow__node.selected > * { border-color: inherit !important; }
.react-flow__node-PROCESO_REF, .react-flow__node-DOCUMENTO { cursor: pointer !important; }
.react-flow__edge-path {
  stroke: #B0B8C8 !important;
}
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function FlowViewerCanvas({
  nodes,
  edges,
  phases,
  swimlanes,
  onFitToCanvas,
  onInit,
  onNavigateDocument,
  onNavigateProcess,
  procesoId,
}: FlowViewerCanvasProps) {
  const { setViewport } = useReactFlow();
  const nodeTypes = useMemo(() => NODE_TYPE_MAP, []);
  const edgeTypes = useMemo(() => EDGE_TYPE_MAP, []);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [minZoom, setMinZoom] = useState(0.05);

  const fitToCanvasLocal = useCallback(() => {
    const el = wrapperRef.current?.querySelector('.react-flow') as HTMLElement | null;
    if (!el) return;
    const zoom = (el.clientWidth - 48) / A3_W;
    setViewport({ x: 24, y: 24, zoom }, { duration: 0 });
  }, [setViewport]);

  const handleInit = useCallback(() => {
    setTimeout(() => {
      const el = wrapperRef.current?.querySelector('.react-flow') as HTMLElement | null;
      if (el) {
        const mz = (el.clientWidth - 48) / A3_W;
        setMinZoom(mz);
      }
      onInit?.(fitToCanvasLocal);
    }, 50);
  }, [onInit, fitToCanvasLocal]);

  // Map Swimlane[] to SwimlaneRenderLane[]
  const swimlaneRenderLanes = useMemo(() => {
    if (!swimlanes || swimlanes.length === 0) return [];
    return swimlanes.map((l) => ({
      id: l.id,
      name: l.puestoNombre,
      y: l.y,
      height: l.height,
    }));
  }, [swimlanes]);

  // ─── Request counts per activity node ───
  const [requestCounts, setRequestCounts] = useState<Record<string, number>>({});
  useEffect(() => {
    if (!procesoId) return;
    request<{ data?: Record<string, number> }>(`/requests/solicitudes/proceso/${procesoId}/counts`)
      .then((body) => {
        if (body?.data) setRequestCounts(body.data);
      })
      .catch(() => {});
  }, [procesoId]);

  const enrichedNodes = useMemo(() => {
    if (Object.keys(requestCounts).length === 0) return nodes;
    return nodes.map((n) => {
      const count = requestCounts[n.id];
      if (!count) return n;
      return { ...n, data: { ...n.data, requestCount: count } };
    });
  }, [nodes, requestCounts]);

  // ─── Tooltip card state (replaces old dark tooltip) ───
  const [hoverNode, setHoverNode] = useState<{
    node: FlowNode;
    pos: { x: number; y: number };
  } | null>(null);

  // ─── Detail panel state ───
  const [selectedNode, setSelectedNode] = useState<{
    node: FlowNode;
    initialPos: { x: number; y: number };
  } | null>(null);

  // ─── Mouse handlers ───
  const onNodeMouseEnter: NodeMouseHandler = useCallback((event, node) => {
    // PROCESO_REF / DOCUMENTO: no mostrar tooltip, solo cursor mano (CSS)
    if (node.type === 'PROCESO_REF' || node.type === 'DOCUMENTO') return;
    const cr = wrapperRef.current?.getBoundingClientRect();
    if (!cr) return;
    // Buscar el elemento DOM del nodo para anclar el tooltip a su esquina
    const target = (event.target as HTMLElement).closest('.react-flow__node') as HTMLElement | null;
    const nr = target?.getBoundingClientRect();
    if (nr) {
      // Esquina superior derecha del nodo
      let x = nr.right - cr.left + 8;
      const y = nr.top - cr.top;
      // Si no cabe a la derecha, esquina superior izquierda
      if (x + 220 > cr.width - 4) x = nr.left - cr.left - 228;
      const safeY = Math.max(4, Math.min(y, cr.height - 80));
      setHoverNode({ node: node as FlowNode, pos: { x, y: safeY } });
    } else {
      // Fallback al cursor
      const rawX = event.clientX - cr.left + 16;
      const rawY = event.clientY - cr.top - 20;
      const safeX = rawX + 220 > cr.width - 4 ? event.clientX - cr.left - 236 : rawX;
      const safeY = Math.max(4, Math.min(rawY, cr.height - 80));
      setHoverNode({ node: node as FlowNode, pos: { x: safeX, y: safeY } });
    }
  }, []);

  const onNodeMouseLeave: NodeMouseHandler = useCallback(() => {
    setHoverNode(null);
  }, []);

  const onNodeClick: NodeMouseHandler = useCallback(
    (event, node) => {
      setHoverNode(null);
      // PROCESO_REF: navegar al proceso referenciado
      if (node.type === 'PROCESO_REF') {
        const codigo = (node.data as FlowNodeData).procesoCodigo as string | undefined;
        if (codigo && onNavigateProcess) {
          onNavigateProcess(codigo);
          return;
        }
      }
      // DOCUMENTO: navegar al documento referenciado
      if (node.type === 'DOCUMENTO') {
        const docCodigo = (node.data as FlowNodeData).documentoCodigo as string | undefined;
        if (docCodigo && onNavigateDocument) {
          onNavigateDocument(docCodigo);
          return;
        }
      }
      const e = event as unknown as React.MouseEvent;
      setSelectedNode({
        node: node as FlowNode,
        initialPos: (() => {
          const panelW = 300;
          const margin = 80;
          const spaceRight = window.innerWidth - e.clientX;
          const x =
            spaceRight > panelW + margin
              ? e.clientX + margin
              : Math.max(20, e.clientX - panelW - margin);
          const y = Math.min(Math.max(e.clientY - 60, 20), window.innerHeight - 400);
          return { x, y };
        })(),
      });
    },
    [onNavigateProcess, onNavigateDocument],
  );

  const onPaneClick = useCallback(() => {
    setHoverNode(null);
    setSelectedNode(null);
  }, []);

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
      <style dangerouslySetInnerHTML={{ __html: VIEWER_STYLES }} />
      <ReactFlow
        nodes={enrichedNodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        selectNodesOnDrag={false}
        onNodeMouseEnter={onNodeMouseEnter}
        onNodeMouseLeave={onNodeMouseLeave}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onInit={handleInit}
        minZoom={minZoom}
        maxZoom={3}
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{
          type: 'orthogonal',
          style: { strokeWidth: 1.5, stroke: '#B0B8C8' },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#B0B8C8', width: 14, height: 14 },
        }}
        nodeExtent={[
          [0, 0],
          [A3_W - T.node.width, A3_H - T.node.height],
        ]}
        style={{ background: '#FFFFFF' }}
      >
        {/* Swimlanes + Phases background layer (read-only) */}
        {(swimlaneRenderLanes.length > 0 || (phases && phases.length > 0)) && (
          <Panel
            position="top-left"
            style={{
              margin: 0,
              padding: 0,
              pointerEvents: 'none',
              position: 'absolute',
              inset: 0,
              zIndex: 0,
            }}
          >
            <SwimlaneViewportLayerViewer
              lanes={swimlaneRenderLanes}
              phases={phases as FlowPhase[]}
            />
          </Panel>
        )}

        <CustomViewerControls onFitToCanvas={onFitToCanvas} />
      </ReactFlow>

      {/* Tooltip — premium 4-section card */}
      {hoverNode && (
        <NodeTooltip data={hoverNode.node.data as FlowNodeData} position={hoverNode.pos} visible />
      )}

      {/* Detail Panel — arrastrable */}
      {selectedNode && (
        <NodeDetailPanel
          node={selectedNode.node}
          onClose={() => setSelectedNode(null)}
          initialPosition={selectedNode.initialPos}
          onNavigateDocument={onNavigateDocument}
          onNavigateProcess={onNavigateProcess}
          procesoId={procesoId}
        />
      )}
    </div>
  );
}
